import React, { useEffect, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { tenantAPI, Tenant, TenantReport } from '../services/api';
import Icon from '../components/Icon';
import ChannelBubble from '../components/ui/ChannelBubble';
import Empty from '../components/ui/Empty';
import { fmt, fmtDate } from '../utils/format';
import { useT } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';

/** First / last day of the current month as YYYY-MM-DD (for the default period). */
function monthRange(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(now) };
}

const Reports: React.FC = () => {
  const t = useT();
  const { role } = useAuth();
  const def = monthRange();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState<number | ''>('');
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);

  const [report, setReport] = useState<TenantReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  const reportRef = useRef<HTMLDivElement>(null);

  // RSD money formatting.
  const rsd = (n: number) => `${fmt(Math.round((n + Number.EPSILON) * 100) / 100)} RSD`;

  useEffect(() => {
    tenantAPI
      .getAll()
      .then((res) => {
        if (res.data.success) {
          setTenants(res.data.data.tenants);
          if (res.data.data.tenants.length) setTenantId(res.data.data.tenants[0].id);
        }
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const generate = async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const res = await tenantAPI.getReport(Number(tenantId), { from, to });
      if (res.data.success) setReport(res.data.data);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    const el = reportRef.current;
    if (!el || !report) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let pos = 0;
      pdf.addImage(img, 'PNG', 0, pos, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        pos = heightLeft - imgH;
        pdf.addPage();
        pdf.addImage(img, 'PNG', 0, pos, imgW, imgH);
        heightLeft -= pageH;
      }
      pdf.save(`izvestaj-${report.tenant.slug}-${from}_${to}.pdf`);
    } catch (e: any) {
      setError(e.message || 'PDF error');
    } finally {
      setPdfBusy(false);
    }
  };

  const totals = report?.totals;

  if (role !== 'super_admin') {
    return (
      <div className="page">
        <div className="card card-pad">
          <strong>{t('Pristup odbijen.', 'Access denied.')}</strong> {t('Ova stranica je dostupna samo super administratorima.', 'This page is only available to super administrators.')}
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Izveštaji potrošnje', 'Spend reports')}</h1>
          <p className="muted">{t('Generiši izveštaj potrošnje po klijentu za izabrani period.', 'Generate a per-tenant spend report for a period.')}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="card card-pad" style={{ marginBottom: 'var(--gap)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, alignItems: 'end' }}>
          <div className="field" style={{ margin: 0 }}>
            <label className="label">{t('Klijent', 'Tenant')}</label>
            <select className="select" value={tenantId} onChange={(e) => setTenantId(e.target.value ? Number(e.target.value) : '')}>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>{tn.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label className="label">{t('Od', 'From')}</label>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label className="label">{t('Do', 'To')}</label>
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={!tenantId || loading} onClick={generate}>
            <Icon name="trendUp" size={16} /> {loading ? t('Generišem…', 'Generating…') : t('Generiši', 'Generate')}
          </button>
        </div>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 'var(--gap)' }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}

      {!report && !loading && !error && (
        <Empty icon="trendUp" title={t('Nema izveštaja', 'No report yet')} sub={t('Izaberi klijenta i period, pa klikni „Generiši".', 'Pick a tenant and a period, then click "Generate".')} />
      )}

      {report && totals && (
        <>
          <div className="flex items-center justify-end" style={{ marginBottom: 12 }}>
            <button className="btn btn-secondary" disabled={pdfBusy} onClick={downloadPdf}>
              <Icon name="download" size={16} /> {pdfBusy ? t('Pripremam PDF…', 'Preparing PDF…') : t('Preuzmi PDF', 'Download PDF')}
            </button>
          </div>

          {/* Captured area for the PDF — modern invoice, forced light so the export is clean in any theme */}
          <div ref={reportRef} style={{ background: '#ffffff', color: '#0f172a', borderRadius: 'var(--r-md)', padding: 36, border: '1px solid #e2e8f0' }}>
            {/* Invoice header: brand + document meta */}
            <div className="flex items-start justify-between wrap gap-16" style={{ marginBottom: 26 }}>
              <div className="flex items-center gap-12">
                <img src="/icon-192.png" alt="Vemid Sendly" width={46} height={46} style={{ borderRadius: 11 }} />
                <div>
                  <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: -0.3 }}>Vemid <span style={{ color: 'var(--brand-600)' }}>Sendly</span></div>
                  <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{t('Omnichannel platforma za kampanje', 'Omnichannel campaign platform')}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--brand-700)', letterSpacing: 0.5 }}>{t('IZVEŠTAJ POTROŠNJE', 'SPEND REPORT')}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{t('Br.', 'No.')} VS-{report.tenant.id}-{from.replace(/-/g, '')}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{t('Datum', 'Date')}: {fmtDate(new Date().toISOString())}</div>
              </div>
            </div>

            {/* Bill-to / period band */}
            <div className="flex items-start justify-between wrap gap-16" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 26 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.6, marginBottom: 5 }}>{t('ZA KLIJENTA', 'BILL TO')}</div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>{report.tenant.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{report.tenant.slug}.vemid.rs</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.6, marginBottom: 5 }}>{t('PERIOD', 'PERIOD')}</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{fmtDate(report.period.from)} – {fmtDate(report.period.to)}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{report.campaigns.length} {t('kampanja', 'campaigns')}</div>
              </div>
            </div>

            {/* Summary tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
              {([
                ['email', t('Email', 'Email'), totals.email],
                ['sms', 'SMS', totals.sms],
                ['viber', 'Viber', totals.viber],
              ] as const).map(([ch, label, val]) => (
                <div key={ch} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                  <div className="flex items-center gap-8" style={{ marginBottom: 8 }}>
                    <ChannelBubble channel={ch} size="sm" /> <span style={{ fontWeight: 700, fontSize: 13 }}>{label}</span>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 20 }}>{rsd(val)}</div>
                </div>
              ))}
              <div style={{ border: '1px solid var(--brand-600)', background: 'var(--brand-50)', borderRadius: 12, padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: 'var(--brand-700)' }}>{t('UKUPNO', 'TOTAL')}</div>
                <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--brand-700)' }}>{rsd(totals.grandTotal)}</div>
                {totals.grandTotalEur != null && (
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--brand-600)', marginTop: 2 }}>≈ €{totals.grandTotalEur.toFixed(2)}</div>
                )}
              </div>
            </div>

            {/* Line items */}
            <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 10, color: '#64748b' }}>
              {t('Stavke', 'Line items')}
            </div>
            {report.campaigns.length === 0 ? (
              <div style={{ color: '#64748b', padding: '14px 0', fontSize: 14 }}>{t('Nema poslatih kampanja u izabranom periodu.', 'No sent campaigns in the selected period.')}</div>
            ) : (
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>{t('Kampanja', 'Campaign')}</th>
                    <th>{t('Datum', 'Date')}</th>
                    <th style={{ textAlign: 'right' }}>{t('Primaoci', 'Recipients')}</th>
                    <th style={{ textAlign: 'right' }}>Email</th>
                    <th style={{ textAlign: 'right' }}>SMS</th>
                    <th style={{ textAlign: 'right' }}>Viber</th>
                    <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.campaigns.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="flex items-center gap-8">
                          <ChannelBubble channel={c.channel} size="sm" />
                          <span style={{ fontWeight: 600 }}>{c.name}</span>
                        </div>
                      </td>
                      <td style={{ color: '#64748b' }}>{fmtDate(c.createdAt)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{fmt(c.recipients)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{c.emailCost ? rsd(c.emailCost) : '—'}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{c.smsCost ? rsd(c.smsCost) : '—'}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{c.viberCost ? rsd(c.viberCost) : '—'}</td>
                      <td className="tnum" style={{ textAlign: 'right', fontWeight: 700 }}>{rsd(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                    <td colSpan={3} style={{ textAlign: 'right' }}>{t('Zbir', 'Subtotal')}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{rsd(totals.email)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{rsd(totals.sms)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{rsd(totals.viber)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{rsd(totals.subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'right', color: '#64748b' }}>{t('Naknada za uslugu', 'Service fee')}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{rsd(totals.serviceFee)}</td>
                  </tr>
                  <tr style={{ fontWeight: 800, fontSize: 14.5 }}>
                    <td colSpan={6} style={{ textAlign: 'right', color: 'var(--brand-700)' }}>{t('UKUPNO ZA NAPLATU', 'GRAND TOTAL')}</td>
                    <td className="tnum" style={{ textAlign: 'right', color: 'var(--brand-700)' }}>
                      {rsd(totals.grandTotal)}
                      {totals.grandTotalEur != null && (
                        <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--brand-600)' }}>≈ €{totals.grandTotalEur.toFixed(2)}</div>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}

            {/* Invoice footer — developer / company info */}
            <div style={{ marginTop: 30, paddingTop: 16, borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#475569' }}>Vemid Sendly</div>
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>
                {t('Razvio', 'Developed by')}: Vemid · vemid.rs · © {new Date().getFullYear()} {t('Sva prava zadržana.', 'All rights reserved.')}
              </div>
              <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 6 }}>
                {t('Cene po poruci', 'Per-message prices')}: Email {rsd(report.prices.email)} · SMS {rsd(report.prices.sms)} · Viber {rsd(report.prices.viber)}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
